import { ElementType, IQuestionConfig } from "src/app/ui-testrunner/models";
import { IItemContentDiff, ILegacyItemContentDiff } from "../util/content-diff";
import { TNumericFilterMethods } from "../util/content-analysis";
import { EFilterMethods, IAuditCheckByFunctions, IQuestionScoreCategory, TAuditCheck } from "./audits";
import { DimensionType, IAssembledPanel, IAssessmentPartition, IPanelModulesConfig, IQuadrantTestlet } from "../../item-set-editor/models/assessment-framework";
import { ExpectedAnswer } from "../../item-set-editor/models";

export enum ECheckCache {
  CLONED_QUESTION_REF = 'CLONED_QUESTION_REF',
  UNRESOLVED_COMMENTS = 'COMMENTS',
  TESTLETS_BY_QUESTION_ID = 'TESTLETS_BY_QUESTION_ID',
  EXPECTED_ANSWER = 'EXPECTED_ANSWER',
  ITEM_META_MAP = 'ITEM_META_MAP',
  SCORE_CATEGORIES = 'SCORE_CATEGORIES',
  EXISTING_BOOKMARKS_MAP = 'EXISTING_BOOKMARKS_MAP',
  LINEAR_SECTION_BY_QUESTION_ID = 'LINEAR_SECTION_BY_QUESTION_ID'
}

export type TCheckCacheType = {
   [ECheckCache.CLONED_QUESTION_REF]: Set<string> // return all the clone labels found in item bank
   [ECheckCache.UNRESOLVED_COMMENTS]: {unresolvedQIds: number[], unresolvedAssetIds: number[]} // returns all questions/assets with unresolved comments
   [ECheckCache.TESTLETS_BY_QUESTION_ID]: Map<number, IQuadrantTestlet[]>;
   [ECheckCache.EXPECTED_ANSWER]: Map<number, Map<string, ExpectedAnswer[]>>;
   [ECheckCache.ITEM_META_MAP]: Map<number, any>;
   [ECheckCache.SCORE_CATEGORIES]: Map<number, Map<string, IQuestionScoreCategory>>;
   [ECheckCache.EXISTING_BOOKMARKS_MAP]: Map<string, string[]>;
   [ECheckCache.LINEAR_SECTION_BY_QUESTION_ID]: Map<number, Set<Number>>;
}

export interface ICheckSpecificDefinitions {
  [ECheckFunctionIds.CONTAINS_NOT_ALLOWED_RESPONSE_BLOCKS]: { allowedRespondables: ElementType[] };
  [ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC]: {pathToPropA: string, pathToPropB: string, comparisonMethod: TNumericFilterMethods, targetBlock: ElementType, arrayFilters?: ITargetPropArrayFilterConfig[], targetBlockFilters?: IFilterConfig[]} // will compare numeric property if array it'll use the length, if non numeric/array will ignore
  [ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC]: {pathToPropA: string, val: number, comparisonMethod: TNumericFilterMethods, targetBlock: ElementType, arrayFilters?: ITargetPropArrayFilterConfig[], targetBlockFilters?: IFilterConfig[]} // will compare numeric property if array it'll use the length, if non numeric/array will ignore
  [ECheckFunctionIds.ELEMENT_COUNT]: { val?: number, comparisonMethod?: TNumericFilterMethods, targetBlocks: ElementType[], arrayFilters?: ITargetPropArrayFilterConfig[]} 
  [ECheckFunctionIds.RESPONDABLE_COUNT]: { val: number, comparisonMethod: TNumericFilterMethods, excludedBlocks?: ElementType[], targetBlocks?: ElementType[], arrayFilters?: ITargetPropArrayFilterConfig[]} 
  [ECheckFunctionIds.INCORRECTLY_MAPPED_SCAN_SLUG]: { scanParam: string, sectionToValidSSlugMap: {[key: string]: string[]}, sectionToValidSlugMap: {[key:number]: string[]}} // will compare numeric property if array it'll use the length, if non numeric/array will ignore
  [ECheckFunctionIds.FRAMEWORK_CONFIG]: IFilterConfig
  [ECheckFunctionIds.EXPECTED_ANSWER]: {arrayFilters?: IFilterConfig[], langs?: string[], count?: number, comparisonMethod?: TNumericFilterMethods};
  [ECheckFunctionIds.HAS_RESPONDABLE]: {inverse?:boolean, excludeHumanScored?: boolean};
  [ECheckFunctionIds.INCORRECT_NUMBER_OF_MODULES]: {updateCaption?: boolean, auditSlug?:string, checkId?: string};
  [ECheckFunctionIds.MISALIGNED_ITEM_COUNT_BETWEEN_MODULES]: {moduleIds: number[], includeFT?: boolean};
  [ECheckFunctionIds.MISALIGNED_META_SUM_BETWEEN_MODULES]: {moduleIds: number[], targetParam: string};
  [ECheckFunctionIds.INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE]: {auditSlug:string, checkId: string};
  [ECheckFunctionIds.PANEL_NAME_INCLUDES]: {targetString: string, includeDisabled?: boolean};
  [ECheckFunctionIds.Q_PARAM_MISMATCH]: {compositeParam: string, componentParam: string, indexStart: number};
  [ECheckFunctionIds.COMPARE_WEIGHT_TO_PARAM]: {param: string, comparisonMethod: TNumericFilterMethods}
  [ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM]: {param: string, comparisonMethod: TNumericFilterMethods, expectedAnsTarget: 'score' | 'weight', aggregate: 'min' | 'max'}
  [ECheckFunctionIds.INVALID_BOOKMARK_TARGET]: {excludeTrailing?: boolean, onlyTrailing?: boolean} // excludeTrailing: won't flag invalid ones due to trailing, onlyTrailing: will only flag invalid due to trailing
  [ECheckFunctionIds.INVALID_READING_PASSAGE]: {excludeTrailing?: boolean, onlyTrailing?: boolean} // excludeTrailing: won't flag invalid ones due to trailing, onlyTrailing: will only flag invalid due to trailing
  [ECheckFunctionIds.Q_PARAM_LINKS_TO_MISSING_QUESTION]: {param:string}
  [ECheckFunctionIds.Q_PSYCH_STATS_MISALIGNED_BETWEEN_LINKED_QUESTIONS]: {param:string}
  [ECheckFunctionIds.CORRECT_NUMBER_OF_OP_ITEMS]: {auditSlug:string, checkId: string, reduceCountIfParamChecked?: {[key:string]: number}, reduceCountByQuadrant?: {[key:string]: number}, reduceCountParamCheckedVal?: {[key:string]: {targetVal: any, reduceBy: number}}, filterOutQuadrantsByConstraints?: {[key: string]: {filterMethod: EFilterMethods, targetValue?: any}}}
  [ECheckFunctionIds.PANEL_HAS_INCORRECT_NUM_SECTION]: {auditSlug:string, checkId: string}
  [ECheckFunctionIds.INCORRECT_NUM_OF_UNIQUE_PANELS]: {auditSlug:string, checkId: string}
  [ECheckFunctionIds.SCORING_CONFIG]: {arrayFilters?: IFilterConfig[], count?: number, comparisonMethod?: TNumericFilterMethods, target: string};
  [ECheckFunctionIds.MISALIGNED_IRT_SCALE_CODE]: {validScales: string[]};
  [ECheckFunctionIds.MISSING_PSYCH_STAT_ROW]: {rowChecks:{stat: string, comparisonMethod: EFilterMethods, targetVal?: any}[]};
  [ECheckFunctionIds.PARAM_VALIDATION]: {includedParamType: DimensionType[], filterMethod: EFilterMethods, filterVal?: any, flagMessage: string, doNotSaveToParamMap: boolean};
  [ECheckFunctionIds.PARAM_TYPE_VALIDATION]: {includeParamType: DimensionType, flagMessage: string, doNotSaveToParamMap: boolean};
  [ECheckFunctionIds.MISALIGNED_READING_PASSAGE]: {labelSplitRegex?: string}
}

interface IDefaultCheckParams {
  checkId?: string,
  auditSlug?: string
}

export type TSpecialParams = {
  [K in keyof ICheckSpecificDefinitions]: ICheckSpecificDefinitions[K] & IDefaultCheckParams;
};

export interface IAuditReturnVal {
  [ECheckFunctionIds.ASSETS_W_OUTSTANDING_CMTS]: number[];
  [ECheckFunctionIds.PUBLISHED_CONTENT_DIFF_AUDIT]: ILegacyItemContentDiff[];
  [ECheckFunctionIds.CURRENT_CONTENT_DIFF_AUDIT]: IItemContentDiff[];
  [ECheckFunctionIds.FRAMEWORK_CONFIG]: boolean[];
  [ECheckFunctionIds.INCORRECT_NUMBER_OF_MODULES]: IPanelModulesConfig[];
  [ECheckFunctionIds.MISALIGNED_ITEM_COUNT_BETWEEN_MODULES]: IPanelModulesConfig[];
  [ECheckFunctionIds.MISALIGNED_META_SUM_BETWEEN_MODULES]: IPanelModulesConfig[];
  [ECheckFunctionIds.INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE]: readonly[];
  [ECheckFunctionIds.PANEL_NAME_INCLUDES]: (IAssembledPanel | IPanelModulesConfig)[];
  [ECheckFunctionIds.MISSING_LINEAR_SECTIONS]: {id:null}[];
  [ECheckFunctionIds.EMPTY_LINEAR_SECTIONS]: IAssessmentPartition[];
  [ECheckFunctionIds.CORRECT_NUMBER_OF_OP_ITEMS]: (IAssembledPanel | IPanelModulesConfig | {})[];
  [ECheckFunctionIds.PANEL_HAS_REPEATED_QUESTION]: (IAssembledPanel | IPanelModulesConfig | {})[];
  [ECheckFunctionIds.PANEL_HAS_INCORRECT_NUM_SECTION]: (IAssembledPanel | IPanelModulesConfig | {})[];
  [ECheckFunctionIds.INCORRECT_NUM_OF_UNIQUE_PANELS]: (IAssembledPanel | IPanelModulesConfig)[];
  [ECheckFunctionIds.PANELS_WITH_ITEM_ROUTING_EXCLUSIONS]: IPanelModulesConfig[]
}

export interface ITargetPropArrayFilterConfig extends IFilterConfig{
  configTarget?: 'pathToPropA' | 'pathToPropB', // what value were filtering
}

export interface IFilterConfig{
  target: string, // what is the target property of the element in the array
  filterMethod: EFilterMethods,
  val?: string | number | boolean | any[];
  isValPath?: boolean; // is the value we're comparing it too another property of the element in the array
}

// Apply that logic to the object
export type TCheckFunctionType = {
  [K in ECheckFunctionIds]: (
    targets: IQuestionConfig[],
    params: K extends keyof TSpecialParams ? TSpecialParams[K] : null,
    check: Partial<IAuditCheckByFunctions>
  ) => Promise<K extends keyof IAuditReturnVal ? IAuditReturnVal[K] : IQuestionConfig[]>;
};

export enum ECheckFunctionIds {
  UTIL_TESTLET = 'UTIL_TESTLET',
  UTIL_QUAD = 'UTIL_QUAD',
  Q_LABEL_DUPE = 'Q_LABEL_DUPE',
  UNTRACKED_ENTRIES = 'UNTRACKED_ENTRIES',
  Q_CLONES_MISSING_PARAM = 'Q_CLONES_MISSING_PARAM',
  Q_CLONES_INVALID_PARAM = 'Q_CLONES_INVALID_PARAM',
  MISSING_OVERALL_VOICE = 'MISSING_OVERALL_VOICE',
  MISSING_OPTION_VOICE = 'MISSING_OPTION_VOICE',
  CONTAIN_DUPLICATED_OPTION_VOICE = 'CONTAIN_DUPLICATED_OPTION_VOICE',
  IMAGES_MISSING_ALT_TEXT = 'IMAGES_MISSING_ALT_TEXT',
  CONTAINS_NOT_ALLOWED_RESPONSE_BLOCKS = 'CONTAINS_NOT_ALLOWED_RESPONSE_BLOCKS',
  QS_W_OUTSTANDING_CMTS = 'QS_W_OUTSTANDING_CMTS',
  ASSETS_W_OUTSTANDING_CMTS = 'ASSETS_W_OUTSTANDING_CMTS',
  CURRENT_CONTENT_DIFF_AUDIT = 'CURRENT_CONTENT_DIFF_AUDIT',
  PUBLISHED_CONTENT_DIFF_AUDIT = 'PUBLISHED_CONTENT_DIFF_AUDIT',
  FIELD_ITEMS_EXCLUDED_IN_TEST_DESIGNS = 'FIELD_ITEMS_EXCLUDED_IN_TEST_DESIGNS',
  FIELD_ITEMS_IN_FT_TESTLETS_IN_NON_FT_TESTLET = 'FIELD_ITEMS_IN_FT_TESTLETS_IN_NON_FT_TESTLET',
  DRAG_DROP_MISSING_TARGETS_INSERTION = 'DRAG_DROP_MISSING_TARGETS_INSERTION',
  COMPARE_ELEMENT_PROPERTY_NUMERIC = 'COMPARE_ELEMENT_PROPERTY_NUMERIC',
  COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC = 'COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC',
  ELEMENT_COUNT = 'ELEMENT_COUNT',
  CONTAINS_DUAL_TEXT_INPUT = 'CONTAINS_DUAL_TEXT_INPUT',
  INCORRECTLY_MAPPED_SCAN_SLUG = 'INCORRECTLY_MAPPED_SCAN_SLUG',
  FRAMEWORK_CONFIG = 'FRAMEWORK_CONFIG',
  RESPONDABLE_COUNT = 'RESPONDABLE_COUNT',
  HAS_RESPONDABLE = 'HAS_RESPONDABLE',
  EXPECTED_ANSWER = 'EXPECTED_ANSWER',
  ITEMS_MISSING_PRESERVED_PARAMS = 'ITEMS_MISSING_PRESERVED_PARAMS',
  INCORRECT_NUMBER_OF_MODULES = 'INCORRECT_NUMBER_OF_MODULES',
  MISALIGNED_ITEM_COUNT_BETWEEN_MODULES = 'MISALIGNED_ITEM_COUNT_BETWEEN_MODULES',
  MISALIGNED_META_SUM_BETWEEN_MODULES = 'MISALIGNED_META_SUM_BETWEEN_MODULES',
  INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE = 'INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE', // generates dynamic checks so it works different than other checks cannot be used in composite checks
  PANEL_NAME_INCLUDES = 'PANEL_NAME_INCLUDES',
  MISSING_LINEAR_SECTIONS = 'MISSING_LINEAR_SECTIONS',
  EMPTY_LINEAR_SECTIONS = 'EMPTY_LINEAR_SECTIONS',
  COUNT_MULTI_SELECT_MCQ_WITH_POSSIBLE_MISSING_EA = 'COUNT_MULTI_SELECT_MCQ_WITH_POSSIBLE_MISSING_EA',
  FORMATTED_RESPONSE_WITH_MULTIPLE_SCORES = 'FORMATTED_RESPONSE_WITH_MULTIPLE_SCORES',
  Q_EXP_ANS_LANG_MISMATCH = 'Q_EXP_ANS_LANG_MISMATCH',
  Q_PARAM_MISMATCH = 'Q_PARAM_MISMATCH',
  SCORE_CATEGORIES_MISCONFIGURED_KEYS = 'SCORE_CATEGORIES_MISCONFIGURED_KEYS',
  SCORE_CATEGORIES_MISSING = 'SCORE_CATEGORIES_MISSING',
  ABS_DIFFERENCE_BETWEEN_SCORE_GREATER_THAN_TWO = 'ABS_DIFFERENCE_BETWEEN_SCORE_GREATER_THAN_TWO',
  NUMBER_OF_SCORE_CATEGORY_MATCHES_THRESHHOLD_PARAM = 'NUMBER_OF_SCORE_CATEGORY_MATCHES_THRESHHOLD_PARAM',
  SCORE_CATEGORY_SEQUENTIAL_NO_MISSING_INTERMEDIATE = 'SCORE_CATEGORY_SEQUENTIAL_NO_MISSING_INTERMEDIATE',
  EXPS_ANS_SCORE_MISSING_FROM_SCORE_CAT = 'EXPS_ANS_SCORE_MISSING_FROM_SCORE_CAT',
  COMPARE_WEIGHT_TO_PARAM = 'COMPARE_WEIGHT_TO_PARAM',
  COMPARE_EA_WEIGHT_TO_PARAM = 'COMPATE_EA_WEIGHT_TO_PARAM',
  MISALIGNED_READING_PASSAGE = 'MISALIGNED_READING_PASSAGE',
  INVALID_READING_PASSAGE = 'INVALID_READING_PASSAGE',
  INVALID_BOOKMARK_TARGET = 'INVALID_BOOKMARK_TARGET',
  INVALID_BOOKMARK_ID = 'INVALID_BOOKMARK_ID',
  Q_PARAM_LINKS_TO_MISSING_QUESTION = 'Q_PARAM_LINKS_TO_MISSING_QUESTION',
  Q_PSYCH_STATS_MISALIGNED_BETWEEN_LINKED_QUESTIONS = 'Q_PSYCH_STATS_MISALIGNED_BETWEEN_LINKED_QUESTIONS',
  CORRECT_NUMBER_OF_OP_ITEMS = 'CORRECT_NUMBER_OF_OP_ITEMS',
  PANEL_HAS_REPEATED_QUESTION = 'PANEL_HAS_REPEATED_QUESTION',
  PANEL_HAS_INCORRECT_NUM_SECTION = 'PANEL_HAS_INCORRECT_NUM_SECTION',
  INCORRECT_NUM_OF_UNIQUE_PANELS = 'INCORRECT_NUM_OF_UNIQUE_PANELS',
  SCORING_CONFIG = 'SCORING_CONFIG',
  MISCONFIGURED_SCORING_SCALE = 'MISCONFIGURED_SCORING_SCALE',
  MISALIGNED_IRT_SCALE_CODE = 'MISALIGNED_IRT_SCALE_CODE',
  USING_NONE_EXISTING_VERSION_CODE = 'USING_NONE_EXISTING_VERSION_CODE',
  NONE_KEYBOARD_ITEMS = 'NONE_KEYBOARD_ITEMS',
  MISSING_PSYCH_STAT_ROW = 'MISSING_PSYCH_STAT_ROW',
  PARAM_VALIDATION = 'PARAM_VALIDATION',
  PARAM_TYPE_VALIDATION = 'PARAM_TYPE_VALIDATION',
  PANELS_WITH_ITEM_ROUTING_EXCLUSIONS = 'PANELS_WITH_ITEM_ROUTING_EXCLUSIONS',
  MISSALIGNED_VALIDATOR_COMBINATION_ID = 'MISSALIGNED_VALIDATOR_COMBINATION_ID',
  DND_DRAGGABLES_WITH_NUMERIC = 'DND_DRAGGABLES_WITH_NUMERIC',
  FT_PLACEMENT_PARAM_CHECK = 'FT_PLACEMENT_PARAM_CHECK'
}
export type TCheckCache = Partial<TCheckCacheType>

